import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Minus, Plus, CaretLeft, CaretRight, CheckCircle, Flag, Trophy, ShareNetwork } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function GroupRoundPlay() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [g, setG] = useState(null);
  const [hole, setHole] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [completing, setCompleting] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/group-rounds/${id}`);
    setG(data);
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line

  if (!g) return <div className="py-10">Loading…</div>;

  const myIdx = g.players.findIndex((p) => p.user_id === user?.id);
  const me = g.players[myIdx];
  const myHole = me.holes[hole] || { strokes: null, par: 4 };
  const par = myHole.par;
  const current = myHole.strokes ?? par;

  const saveStrokes = async (val) => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/group-rounds/${id}/score`, {
        hole_number: hole + 1, strokes: val,
        putts: myHole.putts ?? null, fairway_hit: myHole.fairway_hit ?? null, gir: myHole.gir ?? null,
      });
      setG(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSubmitting(false); }
  };

  const complete = async () => {
    setCompleting(true);
    try {
      await api.post(`/group-rounds/${id}/complete`);
      toast.success("Group round complete — rounds saved to each player");
      navigate("/rounds");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setCompleting(false); }
  };

  const allComplete = g.players.every((p) => p.holes.every((h) => h.strokes != null));

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <span className="label-eyebrow">
            {g.format === "match" ? "Match play" : g.format === "best_ball" ? "Best Ball" : g.format === "scramble" ? "Scramble" : "Stroke play"} · {g.course_name}
            {g.match_allowance_pct != null && g.match_allowance_pct !== 100 && (
              <span className="ml-2 inline-block px-1.5 py-0.5 rounded bg-[#E05D3A]/15 text-[#E05D3A] text-[10px] font-bold" data-testid="group-allowance-chip">
                {g.match_allowance_pct}% allowance
              </span>
            )}
          </span>
          <h1 className="font-heading font-black text-2xl">Hole {hole + 1} <span className="text-[#0F2537]/40">/ 18</span></h1>
        </div>
        {g.format === "match" && g.match && (
          <div className="card-default px-4 py-2 text-right" data-testid="match-status">
            <div className="label-eyebrow">Match</div>
            <div className="font-heading font-bold text-lg">{g.match.label}</div>
          </div>
        )}
        {(g.format === "best_ball" || g.format === "scramble") && g.teams && g.teams.length === 2 && g.teams[0].vs_other && (
          <div className="card-default px-4 py-2 text-right" data-testid="team-match-status">
            <div className="label-eyebrow">Teams</div>
            <div className="font-heading font-bold text-lg">{g.teams[0].vs_other.label}</div>
          </div>
        )}
      </div>

      {/* Hole nav */}
      <div className="flex gap-1.5 overflow-x-auto py-1">
        {Array.from({ length: 18 }).map((_, i) => {
          const filled = me.holes[i]?.strokes != null;
          const active = i === hole;
          return (
            <button key={i} onClick={() => setHole(i)} data-testid={`group-hole-tab-${i+1}`}
              className={`shrink-0 w-9 h-9 rounded-lg text-xs font-bold transition ${
                active ? "bg-[#0F2537] text-white" : filled ? "bg-[#E05D3A]/15 text-[#E05D3A]" : "bg-white border border-[#E2E0D8] text-[#0F2537]/70"
              }`}>{i + 1}</button>
          );
        })}
      </div>

      {/* Your scoring */}
      <div className="card-default p-5 text-center relative overflow-hidden">
        <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-[#E05D3A]/8" />
        <div className="text-sm font-bold text-[#0F2537]/60 flex items-center justify-center gap-2">
          <Flag size={14} weight="fill" /> PAR {par}
          {(me.strokes_received?.[hole] || 0) > 0 && (
            <span className="ml-1">{"●".repeat(me.strokes_received[hole])}</span>
          )}
        </div>
        <div className="text-xs text-[#0F2537]/60 mt-1">Your card · {me.tee_name} tees · CH {me.course_handicap ?? "—"}</div>
        <div className="mt-5 flex items-center justify-center gap-6">
          <button disabled={submitting} onClick={() => saveStrokes(Math.max(1, current - 1))} data-testid="group-strokes-minus" className="score-btn w-16 h-16 flex items-center justify-center"><Minus size={28} weight="bold" /></button>
          <div>
            <div className="font-heading font-black text-7xl tracking-tighter leading-none" data-testid="group-strokes-value">{myHole.strokes ?? par}</div>
            {myHole.strokes != null && (me.stableford?.[hole] != null) && (
              <div className="text-sm font-bold text-[#E05D3A] mt-1">{me.stableford[hole]} pt{me.stableford[hole] === 1 ? "" : "s"}</div>
            )}
          </div>
          <button disabled={submitting} onClick={() => saveStrokes(current + 1)} data-testid="group-strokes-plus" className="score-btn w-16 h-16 flex items-center justify-center"><Plus size={28} weight="bold" /></button>
        </div>
      </div>

      {/* Team standings — best_ball / scramble */}
      {(g.format === "best_ball" || g.format === "scramble") && g.teams && g.teams.length > 0 && (
        <div className="card-default p-5" data-testid="team-standings">
          <span className="label-eyebrow">Team standings</span>
          <ul className="mt-3 space-y-2">
            {g.teams.map((t) => (
              <li key={t.name} data-testid={`team-row-${t.name}`} className={`flex items-center justify-between py-2 px-3 rounded-lg ${t.name === "A" ? "bg-[#E05D3A]/5" : "bg-[#0F2537]/5"}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-white text-xs font-bold ${t.name === "A" ? "bg-[#E05D3A]" : "bg-[#0F2537]"}`}>{t.name}</span>
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate">{t.member_names.join(" + ")}</div>
                    <div className="text-xs text-[#0F2537]/60">{t.holes_played} hole{t.holes_played === 1 ? "" : "s"} played</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="label-eyebrow">Team net</div>
                  <div className="font-heading font-bold text-lg">
                    {t.holes_played > 0 ? t.total_net : "—"}
                    {t.holes_played > 0 && <span className="text-xs text-[#0F2537]/60 ml-1">({t.vs_par >= 0 ? "+" : ""}{t.vs_par})</span>}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* All players summary on this hole */}
      <div className="card-default p-5" data-testid="players-grid">
        <span className="label-eyebrow">Hole {hole + 1} · all players</span>
        <ul className="mt-3 space-y-2">
          {g.players.map((p, i) => {
            const h = p.holes[hole];
            const isMe = p.user_id === user?.id;
            const winner = g.format === "match" && g.match?.per_hole?.[hole];
            const winColor = winner == null ? "" : (i === 0 ? (winner > 0 ? "text-[#1E5631]" : winner < 0 ? "text-[#0F2537]/40" : "") : (winner < 0 ? "text-[#1E5631]" : winner > 0 ? "text-[#0F2537]/40" : ""));
            return (
              <li key={p.user_id} className={`flex items-center justify-between py-2 px-3 rounded-lg ${isMe ? "bg-[#E05D3A]/5" : "bg-white"}`} data-testid={`player-row-${p.user_id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#0F2537] text-white text-xs flex items-center justify-center font-bold">{p.user_name?.slice(0,1).toUpperCase()}</div>
                  <div>
                    <div className="font-semibold text-sm flex items-center gap-1.5">
                      {p.user_name} {isMe && <span className="text-xs text-[#E05D3A]">You</span>}
                      {p.team && (
                        <span data-testid={`player-team-${p.user_id}`} className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-black text-white ${p.team === "A" ? "bg-[#E05D3A]" : "bg-[#0F2537]"}`}>{p.team}</span>
                      )}
                    </div>
                    <div className="text-xs text-[#0F2537]/60">{p.tee_name} · CH {p.course_handicap ?? "—"}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <div className="text-right">
                    <div className="label-eyebrow">Hole</div>
                    <div className={`font-heading font-bold text-lg ${winColor}`}>{h?.strokes ?? "—"}</div>
                  </div>
                  <div className="text-right">
                    <div className="label-eyebrow">Total</div>
                    <div className="font-bold">{p.total_strokes || "—"}</div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="flex items-center gap-3">
        <button onClick={() => setHole(Math.max(0, hole - 1))} disabled={hole === 0} data-testid="group-prev-hole"
          className="px-4 py-3 rounded-xl border border-[#E2E0D8] bg-white font-semibold inline-flex items-center gap-1 disabled:opacity-40">
          <CaretLeft weight="bold" /> Prev
        </button>
        {hole < 17 ? (
          <button onClick={() => setHole(hole + 1)} data-testid="group-next-hole"
            className="flex-1 px-4 py-3 rounded-xl bg-[#0F2537] text-white font-bold inline-flex items-center justify-center gap-1">
            Next hole <CaretRight weight="bold" />
          </button>
        ) : (
          <button onClick={complete} disabled={!allComplete || completing} data-testid="group-complete-btn"
            className="flex-1 px-4 py-3 rounded-xl bg-[#E05D3A] text-white font-bold inline-flex items-center justify-center gap-1 disabled:opacity-60">
            <CheckCircle weight="fill" /> {completing ? "Saving…" : allComplete ? "Complete & save rounds" : "Waiting for all 18 holes…"}
          </button>
        )}
      </div>

      {g.format === "match" && g.match?.holes_played === 18 && (
        <div className="card-default p-5 text-center bg-[#0F2537] text-white">
          <Trophy size={28} weight="fill" className="text-[#E05D3A] mx-auto" />
          <div className="font-heading font-black text-xl mt-2">{g.match.label}</div>
        </div>
      )}

      <a href={`/share/group/${id}`} target="_blank" rel="noreferrer" data-testid="share-group-btn"
        className="inline-flex items-center gap-1.5 text-sm font-bold text-[#0F2537] hover:text-[#E05D3A]">
        <ShareNetwork size={14} weight="bold" /> Share this {g.format === "match" ? "match" : "round"}
      </a>
    </div>
  );
}
